/* ============================================================
   Lost Collective · Journey map (scrollytelling v2, LOS7-2659)
   File: assets/lc-journey-map.js

   Draws the article's custom.journey_geojson on a Mapbox map that the
   scrolly frame resolver (lc-scrollytelling.js) shows in place of a
   photograph for map chapters. One map, one init path, shared with the
   other map surfaces through window.lcMap (lc-map.v2.js): token, LC
   style, colour tokens, pin image.

   Data contract (see docs/journal/scrollytelling-authoring.md):
     LineString + properties.chapter_anchor  -> a leg drawn in that chapter,
       in properties.sequence_in_chapter order; mode "road" solid, "ferry"
       (or dashed:true) dashed. Earlier chapters' legs stay drawn; later
       chapters' legs are removed again when the reader scrolls back up.
     Point + properties.chapter_anchor       -> a waypoint pin that appears
       in that chapter (no leg). properties.label is the map label, cut at
       the first comma. A point whose properties.kind is "stay" caps the
       camera at zoom 11 whenever it is in frame.
     Point + properties.frame_pins:true      -> in that chapter the print
       positions drop as pins (payload.framePins, precision governed by the
       series access_status in Liquid; region_only sends a single centroid).

   Two modes, decided once at init:
     sticky  (motion OK, > 749px): a fixed viewport-filling layer placed
             directly after #lc-scrolly-img so it paints above it, below the
             paper sections (z-index 1) and the article (z-index 2). Shown
             via .is-active by lc-scrollytelling.js's frame resolver; legs
             animate at 60fps by growing the coordinate slice each frame
             (source.setData on a handful of features, no extra layers).
     static  (prefers-reduced-motion, or <= 749px): the container is moved
             inline into #articleBody before the first leg's chapter h2,
             the full route and every pin render at once, no scroll binding.

   Public API: window.LCJourneyMap = { show(anchor), hide() }. show() before
   the map is ready records the anchor and applies it on load.
   ============================================================ */

(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 749px)';
  var REDUCE_MQ = '(prefers-reduced-motion: reduce)';
  var STYLE_URL = 'mapbox://styles/mapbox/light-v11';
  var STAY_MAX_ZOOM = 11;
  var LEG_MAX_ZOOM = 12.5;
  var PINS_ZOOM = 13;
  var CAMERA_MS = 1400;
  // Ceiling for an authored scroll-linked zoom, where the box governs.
  var ZOOM_MAX = 16;

  // The drawn symbol, not the coordinate. journey-wp-img is 110x169 device px
  // registered at pixelRatio 4 (lc-map.v2.js:224) = 27.5 x 42.25 CSS px, and
  // journey-wp-pin sets icon-anchor:'bottom' with no icon-offset and no
  // icon-size, so the icon box's BOTTOM edge sits on the coordinate: the pin
  // reaches 42.25px ABOVE it and 13.75px either side. fitBounds fits the
  // COORDINATE, so a waypoint on a bbox's north edge lands exactly on the
  // padded box's top edge and the pin extends into the padding. Seto's
  // latitude IS camera_home's north edge, which is why its head sat behind the
  // site header. These are SCREEN-space symbols, identical at every zoom in
  // play, so they are pixel constants and must never be folded into the bbox
  // as degrees. 44/14 round up and also cover journey-frame-img.
  var PIN_ABOVE = 44;
  var PIN_SIDE = 14;
  // journey-wp-label is text-anchor:'top', text-offset [0,0.5] at size 12, so
  // it renders about 20px BELOW the coordinate. Nothing draws below a pin, so
  // the bottom edge is cheap; 24 clears the line box plus a descender.
  var LABEL_BELOW = 24;

  // Breathing room. 4.5% of the SHORT side, so it reads the same on a 390
  // phone (17px) and a 1512 laptop (32px); floored so a 320px-tall window
  // keeps a margin, ceilinged so a 2560x1440 window does not spend 65px a side
  // on nothing.
  var BREATHE_FRAC = 0.045;
  var BREATHE_MIN = 12;
  var BREATHE_MAX = 32;
  // What the slack axis gets, and what the clamp may never squeeze past.
  var BREATHE_HARD = 10;

  // The route is never fitted into less than this on either axis, and padding
  // is never allowed to exceed the canvas: Mapbox answers that by logging
  // "Map cannot fit within canvas..." and moving NOTHING, which is today's
  // behaviour on any window under about 220px tall.
  var MIN_CONTENT = 150;
  var MIN_BOX_ABS = 24;

  // .lc-scrolly__caption geometry, used only as a FLOOR under the measured
  // rect and as the fallback when no card can be measured.
  var CARD_W = 520;
  var CARD_VW = 0.05;
  var CARD_MQ = 990;
  // A card is a dodgeable right-hand column only when clearly pushed right.
  // Measured: left-minus-right gap of 455 at 1100 and 829 at 1512 when
  // right-aligned, against 1 at 900 and 0 at 390 when centred.
  var CARD_SIDED = 48;
  var CARD_BAND_MAX = 0.6;

  var REFIT_MS = 150;         // lands after mapbox-gl's own trackResize write

  var lastFit = null;         // {box,maxZoom} or {center,zoom} — what a refit re-runs
  var lastFoot = -1;          // cookie-bar height the live frame was fitted against
  var refitTimer = null;
  var watching = false;

  var api = {
    show: function (anchor) { show(anchor); },
    hide: function () { hide(); },
    // Scroll-linked path. detail: {anchor, visible, t, grow, hold, dash, pins,
    // camera, cameraBox}. Called on every resolver tick for a phased chapter.
    applyPhase: function (detail) { applyPhase(detail); },
  };
  window.LCJourneyMap = api;

  var payload = null;
  var mapEl = null;
  var canvasEl = null;
  var map = null;
  var mode = null;            // 'sticky' | 'static'
  var placed = false;
  var initStarted = false;
  var isReady = false;
  var pendingAnchor = null;
  var currentAnchor = null;
  var order = [];             // chapter slugs in body order
  var legs = [];
  var waypoints = [];
  var pinsBeatIdx = -1;
  var drawn = {};             // legId -> true
  var anim = null;
  // Anchors driven by a scroll-linked phase table instead of the chapter-timed
  // beat. Recorded on first applyPhase and checked at the top of applyBeat, so
  // EVERY applyBeat call site is covered at once, including the map-load replay.
  var phasedAnchors = {};
  var pendingPhase = null;
  var lastCameraKey = null;
  var ochre = '#b88a3a';
  var ink = '#2a2724';

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  function slugOf(text) {
    return String(text || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function resolveMode() {
    if (mode) return mode;
    // A phased article tells its story by scroll on every device, so it takes
    // the sticky path at any width and under reduced motion. There is one map
    // element with one mode, so this is per ARTICLE, not per chapter: on an
    // article carrying a phase table, its other map chapters become sticky on
    // narrow viewports too. Reduced motion stays honoured where it matters,
    // because the line is scroll-driven rather than animated and both camera
    // moves are instant (see cameraTo).
    if (document.querySelector('.lc-scrolly[data-phases]')) {
      mode = 'sticky';
      return mode;
    }
    var reduced = window.matchMedia(REDUCE_MQ).matches;
    var mobile = window.matchMedia(MOBILE_MQ).matches;
    mode = (reduced || mobile) ? 'static' : 'sticky';
    return mode;
  }

  // ----- Geometry ----------------------------------------------------------
  function toRad(d) { return d * Math.PI / 180; }
  function haversineKm(a, b) {
    var R = 6371;
    var dLat = toRad(b[1] - a[1]);
    var dLng = toRad(b[0] - a[0]);
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }
  function cumulative(coords) {
    var cum = [0];
    for (var i = 1; i < coords.length; i++) {
      cum.push(cum[i - 1] + haversineKm(coords[i - 1], coords[i]));
    }
    return cum;
  }
  // Coordinates from the start of the line to fraction f of its length,
  // ending on an interpolated point so the growth is smooth at 60fps.
  function partialLine(coords, cum, f) {
    var total = cum[cum.length - 1];
    if (f >= 1 || total === 0) return coords.slice();
    var target = total * f;
    var out = [coords[0]];
    for (var i = 1; i < coords.length; i++) {
      if (cum[i] <= target) { out.push(coords[i]); continue; }
      var seg = cum[i] - cum[i - 1];
      var t = seg > 0 ? (target - cum[i - 1]) / seg : 0;
      out.push([
        coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * t,
        coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * t,
      ]);
      break;
    }
    if (out.length < 2) out.push(out[0]);
    return out;
  }
  function easeInOut(t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; }
  function bboxOf(points) {
    var b = [[Infinity, Infinity], [-Infinity, -Infinity]];
    points.forEach(function (p) {
      if (p[0] < b[0][0]) b[0][0] = p[0];
      if (p[1] < b[0][1]) b[0][1] = p[1];
      if (p[0] > b[1][0]) b[1][0] = p[0];
      if (p[1] > b[1][1]) b[1][1] = p[1];
    });
    return b;
  }

  // ----- Data --------------------------------------------------------------
  function parsePayload() {
    try {
      var el = document.getElementById('lc-journey-json');
      if (!el) return null;
      var data = JSON.parse(el.textContent || 'null');
      if (!data || !data.geojson || !Array.isArray(data.geojson.features)) return null;
      return data;
    } catch (_e) { return null; }
  }

  function chapterOrder() {
    var root = document.getElementById('articleBody');
    if (!root) return [];
    return Array.prototype.map.call(root.querySelectorAll('h2.h2-mark'), function (h2) {
      var ttl = h2.querySelector('.ttl');
      return slugOf(ttl ? ttl.textContent : h2.textContent);
    });
  }

  function buildModel() {
    order = chapterOrder();
    legs = [];
    waypoints = [];
    pinsBeatIdx = -1;
    payload.geojson.features.forEach(function (f, i) {
      var p = f.properties || {};
      var g = f.geometry || {};
      var anchor = p.chapter_anchor ? slugOf(p.chapter_anchor) : '';
      var beatIdx = anchor ? order.indexOf(anchor) : -1;
      if (g.type === 'LineString' && Array.isArray(g.coordinates) && g.coordinates.length > 1 && anchor) {
        var cum = cumulative(g.coordinates);
        legs.push({
          id: p.leg_id || ('leg-' + i),
          anchor: anchor,
          beatIdx: beatIdx,
          seq: typeof p.sequence_in_chapter === 'number' ? p.sequence_in_chapter : (p.order || i),
          order: typeof p.order === 'number' ? p.order : i,
          mode: (p.mode === 'ferry' || p.dashed === true) ? 'ferry' : 'road',
          from: p.from || '',
          to: p.to || '',
          coords: g.coordinates,
          cum: cum,
          km: cum[cum.length - 1],
        });
      } else if (g.type === 'Point' && Array.isArray(g.coordinates)) {
        var label = String(p.label || '').split(',')[0].trim();
        waypoints.push({
          id: p.waypoint_id || ('wp-' + i),
          label: label,
          kind: p.kind || '',
          anchor: anchor,
          beatIdx: beatIdx,
          framePins: p.frame_pins === true,
          coords: g.coordinates,
        });
        if (p.frame_pins === true && beatIdx >= 0) pinsBeatIdx = beatIdx;
      }
    });
    legs.sort(function (a, b) {
      if (a.beatIdx !== b.beatIdx) return a.beatIdx - b.beatIdx;
      if (a.seq !== b.seq) return a.seq - b.seq;
      return a.order - b.order;
    });
  }

  function waypointById(id) {
    for (var i = 0; i < waypoints.length; i++) if (waypoints[i].id === id) return waypoints[i];
    return null;
  }

  // The pins for the frame beat: exact per-position pins when the series is
  // public (deduplicated on coordinate), otherwise the single centroid the
  // Liquid resolved. A private series sends nothing and draws nothing.
  function framePinFeatures() {
    var feats = [];
    if (payload.access === 'private') return feats;
    if (payload.access === 'public' && Array.isArray(payload.framePins) && payload.framePins.length) {
      var seen = {};
      payload.framePins.forEach(function (pin) {
        var lat = parseFloat(pin.lat), lng = parseFloat(pin.lng);
        if (isNaN(lat) || isNaN(lng)) return;
        var key = lat.toFixed(5) + ',' + lng.toFixed(5);
        if (seen[key]) return;
        seen[key] = true;
        feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: { title: pin.title || '' } });
      });
      return feats;
    }
    var c = payload.centroid;
    if (c && !isNaN(parseFloat(c.lat)) && !isNaN(parseFloat(c.lng))) {
      feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [parseFloat(c.lng), parseFloat(c.lat)] }, properties: { title: payload.label || '' } });
      return feats;
    }
    // No centroid recorded: stand on the frame_pins waypoint itself.
    for (var i = 0; i < waypoints.length; i++) {
      if (waypoints[i].framePins) {
        feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: waypoints[i].coords }, properties: { title: waypoints[i].label } });
        break;
      }
    }
    return feats;
  }

  // ----- Map layers --------------------------------------------------------
  function fc(features) { return { type: 'FeatureCollection', features: features }; }
  function lineFeature(coords, leg, alphas) {
    var o = alphas && typeof alphas[leg.id] === 'number' ? alphas[leg.id] : null;
    var props = { id: leg.id, mode: leg.mode };
    if (o !== null) props.o = Math.max(0, Math.min(1, o)) * 0.9;
    return { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: props };
  }

  function addLayers() {
    var lcMap = window.lcMap;
    ochre = lcMap.lcToken('--lc-ochre') || ochre;
    ink = lcMap.lcToken('--lc-ink') || ink;
    var paper = lcMap.lcToken('--lc-paper') || '#f6f3ec';

    map.addSource('journey-road', { type: 'geojson', data: fc([]) });
    map.addSource('journey-ferry', { type: 'geojson', data: fc([]) });
    map.addSource('journey-wp', { type: 'geojson', data: fc([]) });
    map.addSource('journey-frames', { type: 'geojson', data: fc([]) });

    // 'o' drives a scroll-linked fade the same way the pins' 'a' does: absent,
    // it coalesces to the layer's resting opacity, so a caller that never sets
    // it keeps today's behaviour exactly.
    map.addLayer({
      id: 'journey-road-line',
      type: 'line',
      source: 'journey-road',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': ink, 'line-width': 2.5, 'line-opacity': ['coalesce', ['get', 'o'], 0.9] },
    });

    // Frame pins: a smaller teardrop than the waypoint pin, same drawing.
    if (!map.hasImage('journey-frame-img')) {
      var buildPin = (typeof window.lcBuildPinImage === 'function') ? window.lcBuildPinImage : lcMap.lcBuildPinImage;
      map.addImage('journey-frame-img', buildPin ? buildPin(ochre, ink, 8) : buildPinFallback(8), { pixelRatio: 4 });
    }
    map.addLayer({
      id: 'journey-frame-pin',
      type: 'symbol',
      source: 'journey-frames',
      layout: { 'icon-image': 'journey-frame-img', 'icon-anchor': 'bottom', 'icon-allow-overlap': true, 'icon-ignore-placement': true },
    });

    lcMap.addLCPinLayers(map, 'journey-wp', { idPrefix: 'journey-wp' });
    // Per-pin entrance, scoped to the journey map. The shared builder in
    // lc-map.v2.js is left alone so the PDP and collection maps are untouched:
    // these two overrides simply make the existing layer read an 'a' property
    // (0..1) off each feature. Absent 'a' coalesces to 1, so any caller that
    // does not set it keeps today's behaviour exactly.
    map.setPaintProperty('journey-wp-pin', 'icon-opacity',
      ['coalesce', ['get', 'a'], 1]);
    map.setLayoutProperty('journey-wp-pin', 'icon-size',
      ['interpolate', ['linear'], ['coalesce', ['get', 'a'], 1], 0, 0.7, 1, 1]);
    // The ferry legs draw ABOVE the pins. Both Ikeshima legs end on the same
    // coordinate, but the one arriving from Seto runs in from the north-east,
    // straight up behind a 42px pin that used to cover it, so only the Kōnoura
    // leg reached the point and the two read as docking in different places.
    map.addLayer({
      id: 'journey-ferry-line',
      type: 'line',
      source: 'journey-ferry',
      layout: { 'line-cap': 'butt', 'line-join': 'round' },
      paint: {
        'line-color': ink,
        'line-width': 2,
        'line-opacity': ['coalesce', ['get', 'o'], 0.9],
        'line-dasharray': [1.6, 2.2],
      },
    });
    map.addLayer({
      id: 'journey-wp-label',
      type: 'symbol',
      source: 'journey-wp',
      // Matched to light-v11's own settlement-subdivision-label, read off the
      // live style: DIN Pro Regular, uppercase, 0.15 tracking, 1px halo. A
      // waypoint then reads as a place ON this map rather than an annotation
      // laid over it. A waypoint whose label is blank draws no text at all,
      // which is how an origin the basemap already names stays unlabelled.
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['DIN Pro Regular', 'Arial Unicode MS Regular'],
        'text-size': ['interpolate', ['cubic-bezier', 0.5, 0, 1, 1], ['zoom'], 11, 11, 15, 14],
        'text-transform': 'uppercase',
        'text-anchor': 'top',
        'text-offset': [0, 0.5],
        'text-letter-spacing': 0.15,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-opacity': ['coalesce', ['get', 'a'], 1], 'text-color': ink, 'text-halo-color': paper, 'text-halo-width': 1 },
    });
  }

  // lc-map.v2.js declares lcBuildPinImage as a global function (it is not on
  // window.lcMap); reach it there, and keep a tiny fallback so a missing
  // helper degrades to a plain dot instead of a thrown error.
  function buildPinFallback(r) {
    var s = 4, sr = r * s, w = sr * 2 + 8, h = w;
    var canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.beginPath(); ctx.arc(w / 2, h / 2, sr, 0, Math.PI * 2);
    ctx.fillStyle = ochre; ctx.fill();
    ctx.strokeStyle = ink; ctx.lineWidth = 1.5 * s; ctx.stroke();
    var id = ctx.getImageData(0, 0, w, h);
    return { width: w, height: h, data: id.data };
  }

  // ----- State -------------------------------------------------------------
  // partials is a LIST: the road leg that is growing and, separately, every
  // dashed leg that is drawing itself in. Two ferry routes converging on one
  // island have to advance together, so each carries its own partial rather
  // than sharing the single slot the road used to own.
  function setLines(partials, alphas) {
    var list = partials || [];
    ['road', 'ferry'].forEach(function (m) {
      var feats = [];
      legs.forEach(function (leg) {
        if (leg.mode !== m || !drawn[leg.id]) return;
        feats.push(lineFeature(leg.coords, leg, alphas));
      });
      list.forEach(function (p) {
        if (p && p.leg.mode === m) feats.push(lineFeature(p.coords, p.leg, alphas));
      });
      var src = map.getSource('journey-' + m);
      if (src) src.setData(fc(feats));
    });
  }

  function setPins(activeIdx) {
    var shown = {};
    waypoints.forEach(function (wp) {
      if (wp.beatIdx >= 0 && wp.beatIdx <= activeIdx) shown[wp.id] = wp;
    });
    legs.forEach(function (leg) {
      if (!drawn[leg.id]) return;
      var a = waypointById(leg.from), b = waypointById(leg.to);
      if (a) shown[a.id] = a;
      if (b) shown[b.id] = b;
    });
    var wpFeats = Object.keys(shown).map(function (id) {
      var wp = shown[id];
      return { type: 'Feature', geometry: { type: 'Point', coordinates: wp.coords }, properties: { label: wp.label, id: wp.id } };
    });
    var wpSrc = map.getSource('journey-wp');
    if (wpSrc) wpSrc.setData(fc(wpFeats));

    var frameSrc = map.getSource('journey-frames');
    if (frameSrc) frameSrc.setData(fc(pinsBeatIdx >= 0 && pinsBeatIdx <= activeIdx ? framePinFeatures() : []));
  }

  function cancelAnim() {
    if (anim) { anim.cancelled = true; anim = null; }
  }

  // The surface being padded is the CANVAS, not the window: they differ by the
  // scrollbar (1497 vs 1512, 375 vs 390), enough for a window sized exactly to
  // a breakpoint to take the wrong branch.
  function mapRect() {
    var el = (map && map.getContainer) ? map.getContainer() : canvasEl;
    var r = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (r && r.width > 2 && r.height > 2) {
      return { left: r.left, top: r.top, width: r.width, height: r.height };
    }
    return {
      left: 0, top: 0,
      width: window.innerWidth || document.documentElement.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight
    };
  }

  // How far a fixed or sticky overlay reaches in from one edge, read from the
  // live element. Hard-coding these was the root cause: the header measures
  // 83px at >=990 and 71px below, and the chapter rail's right edge is 235 at
  // >=1280 (the old 260 wasted 24px) and 79 at 990-1279 (the old 72 was short,
  // and the route measurably ran under it).
  function overlayInset(sel, edge, r) {
    var el = document.querySelector(sel);
    if (!el) return 0;
    var b = el.getBoundingClientRect();
    if (b.width < 1 || b.height < 1) return 0;
    var v;
    if (edge === 'top') v = b.bottom - r.top;
    else if (edge === 'left') v = b.right - r.left;
    else v = (r.top + r.height) - b.top;
    return v > 0 ? Math.round(v) : 0;
  }

  // The cookie banner publishes its own live height as --lc-cookie-bar-h on
  // documentElement, 0px when hidden, republished on resize and on the
  // accept/decline decision. Reading it is exact and costs no layout.
  function cookieInset(r) {
    var v = 0;
    try {
      v = parseFloat(window.getComputedStyle(document.documentElement)
        .getPropertyValue('--lc-cookie-bar-h'));
    } catch (_e) { v = NaN; }
    if (!isNaN(v)) return Math.max(0, Math.round(v));
    return overlayInset('#lc-cookie-consent', 'bottom', r);
  }

  // What the caption cards take away from the map, MEASURED.
  //
  // A card counts when it is LAID OUT, not when it is painted. Caption opacity
  // is scroll-linked, and the 'home' camera fires while card 0 is still about
  // 90vh below the fold at opacity 0, so an opacity test would measure no card
  // at exactly the camera this fix is about. Do not add one.
  //
  // Horizontal geometry is stable, so it decides the side. Vertical geometry
  // is not, so the band is taken at the card's resting position, which is
  // where the resolver makes it the active beat and where it is read.
  function captionReserve(r) {
    var block = currentAnchor
      ? document.querySelector('.lc-scrolly[data-anchor="' + currentAnchor + '"]')
      : null;
    var caps = (block || document).querySelectorAll('.lc-scrolly__caption');
    var tall = 0, gapL = Infinity, gapR = Infinity;
    var i, cs, b;
    for (i = 0; i < caps.length; i++) {
      cs = window.getComputedStyle(caps[i]);
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      b = caps[i].getBoundingClientRect();
      if (b.width < 1 || b.height < 1) continue;
      if (b.height > tall) tall = b.height;
      if (b.left - r.left < gapL) gapL = b.left - r.left;
      if ((r.left + r.width) - b.right < gapR) gapR = (r.left + r.width) - b.right;
    }
    var floorCol = Math.round(r.width * CARD_VW) + CARD_W;
    if (tall < 1) {
      return { column: r.width >= CARD_MQ ? floorCol : 0, band: 0 };
    }
    if (gapL - gapR > CARD_SIDED) {
      // Right-hand column: reserve from the canvas edge to the card's LEFT
      // edge, floored by the CSS so a mid-fade card cannot collapse it.
      return { column: Math.max(Math.round(r.width - gapL), floorCol), band: 0 };
    }
    // Centred or full width: no horizontal reserve can dodge it, so the
    // reserve has to be vertical.
    var h = Math.min(tall, r.height * CARD_BAND_MAX);
    return { column: 0, band: Math.round((r.height + h) / 2) };
  }

  // Web Mercator y, normalised, so padding can compare the route's aspect in
  // the same space fitBounds works in.
  function mercY(lat) {
    var l = Math.max(-85.05112878, Math.min(85.05112878, lat));
    return Math.log(Math.tan((90 + l) * Math.PI / 360)) / (2 * Math.PI);
  }

  function mercatorAspect(box) {
    if (!box || box.length !== 2) return 0;
    var dx = Math.abs(box[1][0] - box[0][0]) / 360;
    var dy = Math.abs(mercY(box[1][1]) - mercY(box[0][1]));
    if (!(dx > 0) || !(dy > 0)) return 0;
    return dx / dy;
  }

  // Bring one axis inside MIN_CONTENT, taking the reduction out of the
  // discretionary part and never out of the drawn pin's own clearance. A naive
  // proportional squeeze re-clips the pin on short windows, which is the bug
  // being fixed. If the floors alone exceed the budget the floors win: a
  // squeezed map beats a decapitated pin.
  function clampAxis(a, b, floorA, floorB, extent) {
    var maxPad = extent - MIN_CONTENT;
    var floorSum = floorA + floorB;
    var k;
    if (a + b > maxPad) {
      if (floorSum >= maxPad) { a = floorA; b = floorB; }
      else {
        k = (maxPad - floorSum) / ((a + b) - floorSum);
        a = Math.round(floorA + (a - floorA) * k);
        b = Math.round(floorB + (b - floorB) * k);
      }
    }
    if (a + b > extent - MIN_BOX_ABS) {
      k = (extent - MIN_BOX_ABS) / (a + b);
      a = Math.floor(a * k);
      b = Math.floor(b * k);
    }
    return [a, b];
  }

  // The optional `box` is the bbox about to be fitted; omitting it is valid
  // and simply spends breathing room on both axes, so the old zero-argument
  // contract still works.
  function cameraPadding(box) {
    var r = mapRect();
    var W = r.width;
    var H = r.height;
    var breathe = Math.max(BREATHE_MIN,
      Math.min(BREATHE_MAX, Math.round(Math.min(W, H) * BREATHE_FRAC)));

    var head = overlayInset('#lc-header', 'top', r);
    var rail = overlayInset('.lc-chapter-rail', 'left', r);
    var foot = cookieInset(r);
    var card = captionReserve(r);

    var top = head + PIN_ABOVE;
    var left = rail + PIN_SIDE;
    var right = PIN_SIDE + card.column;
    var plain = foot + LABEL_BELOW;
    var bottom = plain;
    var banded = false;
    if (card.band > 0) {
      bottom = Math.max(plain, card.band + LABEL_BELOW);
      banded = bottom > plain;
    }

    // Breathing goes on the BINDING axis, the one that sets the zoom. Padding
    // the slack axis symmetrically does not change the fit; it only risks
    // flipping which axis binds. The slack axis still gets BREATHE_HARD so no
    // stroke bleeds off an edge.
    var aspect = mercatorAspect(box);
    var cw = W - left - right;
    var ch = H - top - bottom;
    var xBinds = true;
    if (aspect > 0) xBinds = ch > 0 ? (cw / ch) < aspect : false;
    var bx = aspect > 0 ? (xBinds ? breathe : BREATHE_HARD) : breathe;
    var by = aspect > 0 ? (xBinds ? BREATHE_HARD : breathe) : breathe;
    left += bx; right += bx; top += by; bottom += by; plain += by;

    // Take the caption band ONLY while it still leaves a legible frame. Below
    // 990 the card rests across the middle, so the dodge is all or nothing: at
    // 390x844 it clears the card outright, at 900x600 buying a partial dodge
    // would shrink the route to a sliver AND leave more of it covered.
    if (banded && (H - top - bottom) < MIN_CONTENT) bottom = plain;

    var x = clampAxis(left, right,
      PIN_SIDE + BREATHE_HARD, PIN_SIDE + BREATHE_HARD, W);
    var y = clampAxis(top, bottom,
      head + PIN_ABOVE + BREATHE_HARD, LABEL_BELOW + BREATHE_HARD, H);

    lastFoot = foot;
    if (window.LC_SCROLLY_DEBUG) {
      window.__lcFrame = {
        canvas: [Math.round(W), Math.round(H)],
        head: head, rail: rail, foot: foot,
        card: card, breathe: breathe, aspect: aspect, xBinds: xBinds,
        banded: banded && bottom !== plain,
        padding: { top: y[0], right: x[1], bottom: y[1], left: x[0] }
      };
    }
    return { top: y[0], right: x[1], bottom: y[1], left: x[0] };
  }

  function beatGeometry(idx) {
    var pts = [];
    var hasStay = false;
    var single = null;
    legs.forEach(function (leg) {
      if (leg.beatIdx !== idx) return;
      pts = pts.concat(leg.coords);
      if (leg.from === 'stay' || leg.to === 'stay') hasStay = true;
      var a = waypointById(leg.from), b = waypointById(leg.to);
      if ((a && a.kind === 'stay') || (b && b.kind === 'stay')) hasStay = true;
    });
    waypoints.forEach(function (wp) {
      if (wp.beatIdx !== idx) return;
      pts.push(wp.coords);
      if (wp.kind === 'stay') hasStay = true;
      single = wp;
    });
    if (pinsBeatIdx === idx) {
      framePinFeatures().forEach(function (f) { pts.push(f.geometry.coordinates); });
    }
    return { points: pts, hasStay: hasStay, single: pts.length === 1 ? single : null };
  }

  function moveCamera(idx, animate) {
    var geo = beatGeometry(idx);
    if (!geo.points.length) return;
    var duration = animate ? CAMERA_MS : 0;
    if (geo.points.length === 1) {
      var zoom = geo.hasStay ? STAY_MAX_ZOOM : (pinsBeatIdx === idx ? PINS_ZOOM : 12);
      // A lone waypoint is centred on the CANVAS today, i.e. behind the
      // caption card at >=990 and under the header on a short window. offset
      // puts it at the centre of the clear region instead. offset, NOT
      // easeTo({padding}): padding passed to a camera call persists on the
      // transform and would silently colour every later fit.
      var p = cameraPadding(null);
      lastFit = { center: geo.points[0], zoom: zoom };
      map.easeTo({
        center: geo.points[0],
        zoom: zoom,
        offset: [Math.round((p.left - p.right) / 2), Math.round((p.top - p.bottom) / 2)],
        duration: duration,
        essential: true
      });
      return;
    }
    var b = bboxOf(geo.points);
    var maxZoom = geo.hasStay ? STAY_MAX_ZOOM : (pinsBeatIdx === idx ? PINS_ZOOM : LEG_MAX_ZOOM);
    lastFit = { box: b, maxZoom: maxZoom };
    map.fitBounds(b, {
      padding: cameraPadding(b), maxZoom: maxZoom, duration: duration, essential: true
    });
  }

  function applyBeat(anchor, animate) {
    // A phased chapter owns its own line, pin and camera state and is driven
    // entirely by scroll. The chapter-timed beat would force-draw its legs and
    // fit the camera, so refuse here rather than at each call site: this also
    // covers the pendingAnchor replay in the map's load handler, which fires
    // when a reader reloads deep inside the chapter.
    if (phasedAnchors[anchor]) return;
    var idx = order.indexOf(anchor);
    if (idx < 0) return;
    currentAnchor = anchor;
    cancelAnim();
    legs.forEach(function (leg) {
      if (leg.beatIdx >= 0 && leg.beatIdx < idx) drawn[leg.id] = true;
      else if (leg.beatIdx > idx) delete drawn[leg.id];
    });
    var mine = legs.filter(function (leg) { return leg.beatIdx === idx && !drawn[leg.id]; });
    if (!animate) {
      mine.forEach(function (leg) { drawn[leg.id] = true; });
      mine = [];
    }
    setLines(null);
    setPins(idx);
    moveCamera(idx, animate);
    if (mine.length) animateSequence(mine, idx);
  }

  function animateSequence(queue, idx) {
    var token = { cancelled: false };
    anim = token;
    function next() {
      if (token.cancelled) return;
      var leg = queue.shift();
      if (!leg) { anim = null; return; }
      animateLeg(leg, token, function () {
        drawn[leg.id] = true;
        setLines(null);
        setPins(idx);
        next();
      });
    }
    next();
  }

  function animateLeg(leg, token, done) {
    var dur = Math.min(3200, Math.max(1400, 900 + leg.km * 45));
    var start = null;
    function frame(now) {
      if (token.cancelled) return;
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / dur);
      setLines([{ leg: leg, coords: partialLine(leg.coords, leg.cum, easeInOut(t)) }]);
      if (t < 1) window.requestAnimationFrame(frame);
      else done();
    }
    window.requestAnimationFrame(frame);
  }

  // ----- Scroll-linked phases ----------------------------------------------
  // The chapter-timed path grows a leg on a wall clock (animateLeg's dur), so
  // it runs whether or not the reader is scrolling and cannot go backwards.
  // A phased chapter instead receives a 0..1 fraction from the resolver on
  // every tick and slices the leg with partialLine, which is pure and
  // direction-agnostic: a smaller fraction is simply a shorter line, so
  // reversing is free and needs no new geometry.

  function legById(id) {
    for (var i = 0; i < legs.length; i++) {
      if (legs[i].id === id) return legs[i];
    }
    return null;
  }

  function setPhasePins(ids, alphas, lineAlpha) {
    var shown = {};
    (ids || []).forEach(function (id) {
      var wp = waypointById(id);
      if (wp) shown[wp.id] = wp;
    });
    // A completed leg still reveals its own endpoints, which is how the
    // arrival pin appears exactly when the line lands rather than early. A leg
    // that has faded out reveals nothing: it is no longer on the map.
    legs.forEach(function (leg) {
      if (!drawn[leg.id]) return;
      if (lineAlpha && typeof lineAlpha[leg.id] === 'number' && lineAlpha[leg.id] <= 0) return;
      var a = waypointById(leg.from);
      var b = waypointById(leg.to);
      if (a) shown[a.id] = a;
      if (b) shown[b.id] = b;
    });
    var feats = Object.keys(shown).map(function (id) {
      var wp = shown[id];
      // 'a' drives the entrance: icon-opacity reads it directly and icon-size
      // interpolates 0.7 -> 1, so a pin fades up while it grows into place
      // rather than snapping on. Scroll-driven, so it reverses for free.
      var a = alphas && typeof alphas[id] === 'number' ? alphas[id] : 1;
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: wp.coords },
        properties: { label: wp.label, id: wp.id, a: Math.max(0, Math.min(1, a)) },
      };
    });
    var wpSrc = map.getSource('journey-wp');
    if (wpSrc) wpSrc.setData(fc(feats));
    var frameSrc = map.getSource('journey-frames');
    if (frameSrc) frameSrc.setData(fc([]));
  }

  // Everything the camera KEY is answerable for painting, not just the beat
  // that fired it. camera_home is the bbox of the stay-seto leg alone, but a
  // later phase switches on the ikeshima pin outside that box and no camera
  // move fires until 'end': measured screen x of -88.8 at 768x1024 and -52.0
  // at 1100x700, i.e. drawn off-canvas. No padding change can reach a point
  // that is outside the fitted region by construction.
  function drawnPoints(ids) {
    var pts = [];
    var i, j, leg, wp;
    for (i = 0; i < (ids || []).length; i++) {
      wp = waypointById(ids[i]);
      if (wp) { pts.push(wp.coords); continue; }
      leg = legById(ids[i]);
      if (leg && leg.coords) {
        for (j = 0; j < leg.coords.length; j++) pts.push(leg.coords[j]);
      }
    }
    return pts;
  }

  // The authored bbox states the art direction and is honoured as a FLOOR: its
  // corners join the point set, so the union can only ever WIDEN the frame,
  // never narrow it. A caller that passes no draws gets the authored box back
  // untouched.
  function unionBox(box, draws) {
    var pts = drawnPoints(draws);
    if (!pts.length) return box;
    pts.push([box[0][0], box[0][1]]);
    pts.push([box[1][0], box[1][1]]);
    return bboxOf(pts);
  }

  // mapbox-gl keeps its OWN copy of the container size and refreshes it only
  // from its own ResizeObserver. The map is created while the chapter's
  // container is still short, and a fit issued before that observer catches up
  // is computed against the stale size: the frame lands far wider than the box
  // asked for, and mapbox paints only that many pixels, so the rest of the
  // container shows its background and the map reads as a band across the top.
  // A reader who is not scrolling never triggers another fit to correct it.
  function syncSize() {
    if (!map || !canvasEl || !map.transform) return;
    var r = canvasEl.getBoundingClientRect();
    if (!r.width || !r.height) return;
    if (Math.abs(map.transform.width - r.width) > 1 ||
        Math.abs(map.transform.height - r.height) > 1) {
      map.resize();
    }
  }

  function cameraTo(box, animate, draws) {
    if (!box || !box.length) return;
    syncSize();
    var fitBox = unionBox(box, draws);
    var reduce = window.matchMedia && window.matchMedia(REDUCE_MQ).matches;
    // Same ceiling the scroll-linked path uses. cameraTo is only ever reached
    // from a phase, where the box is authored art direction, and a cap below
    // what that box asks for silently reframes it: the identical box fitted
    // through the keyed path and through a zoom would land on two different
    // frames, which is exactly the jump at the end of a zoom into a close-up.
    lastFit = { box: fitBox, maxZoom: ZOOM_MAX };
    var padNow = cameraPadding(fitBox);
    if (window.LC_SCROLLY_DEBUG) {
      window.__lcFitLog = window.__lcFitLog || [];
      window.__lcFitLog.push({ src: 'cameraTo', box: fitBox, pad: padNow, y: window.scrollY });
    }
    map.fitBounds(fitBox, {
      padding: padNow,
      maxZoom: ZOOM_MAX,
      duration: animate && !reduce ? CAMERA_MS : 0,
      essential: true,
    });
  }

  // The camera is keyed, so nothing re-fires on a resize, and cameraPadding is
  // only read at fire time. Meanwhile mapbox-gl's own trackResize DOES resize
  // the canvas, preserving centre and zoom and re-cropping around them. That
  // is how a reader who narrows the window mid-chapter ends up with the route
  // running off the edges, and no padding formula alone can reach it.
  function refitCamera() {
    if (!isReady || !map || !lastFit) return;
    if (resolveMode() === 'static') return;
    syncSize();
    if (lastFit.box) {
      var padR = cameraPadding(lastFit.box);
      if (window.LC_SCROLLY_DEBUG) {
        window.__lcFitLog = window.__lcFitLog || [];
        window.__lcFitLog.push({ src: 'refit', box: lastFit.box, pad: padR, y: window.scrollY });
      }
      map.fitBounds(lastFit.box, {
        padding: padR,
        maxZoom: lastFit.maxZoom,
        duration: 0,
        essential: true
      });
      return;
    }
    var p = cameraPadding(null);
    map.easeTo({
      center: lastFit.center,
      zoom: lastFit.zoom,
      offset: [Math.round((p.left - p.right) / 2), Math.round((p.top - p.bottom) / 2)],
      duration: 0,
      essential: true
    });
  }

  // 150ms: long enough to land AFTER mapbox-gl's own trackResize write so ours
  // wins, short enough that a drag-resize reads as the window settling, and it
  // collapses a resize storm into one measure and one fit. Duration 0 always:
  // a refit is a correction, not a movement, and it re-applies the SAME
  // geographic box, so it never fights a growing leg.
  function scheduleRefit() {
    if (refitTimer) window.clearTimeout(refitTimer);
    refitTimer = window.setTimeout(function () {
      refitTimer = null;
      refitCamera();
    }, REFIT_MS);
  }

  function watchViewport() {
    if (watching) return;
    watching = true;
    if (typeof window.ResizeObserver === 'function' && canvasEl) {
      new window.ResizeObserver(scheduleRefit).observe(canvasEl);
    }
    window.addEventListener('resize', scheduleRefit, { passive: true });
    window.addEventListener('orientationchange', scheduleRefit, { passive: true });
    // The one overlay change that raises no resize event: a reader accepting
    // or declining the cookie bar while stationary, which drops 81-167px of
    // bottom reserve. lc-cookie-consent.js publishes the new height by writing
    // --lc-cookie-bar-h on documentElement, so that attribute mutating is
    // exactly and only that moment. No polling, nothing on the scroll path.
    if (typeof window.MutationObserver === 'function') {
      new window.MutationObserver(function () {
        if (!isReady || !lastFit) return;
        if (Math.abs(cookieInset(mapRect()) - lastFoot) > 2) scheduleRefit();
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    }
  }

  // A camera that follows the scroll rather than jumping between keys. The two
  // frames are interpolated in MERCATOR space, not in raw degrees: latitude
  // degrees are not linear on the map, so lerping them directly makes the zoom
  // drift and then catch up. Fitted with duration 0 every tick, so it tracks
  // the reader exactly and reverses for free, and the padding is recomputed on
  // each fit so it stays correct through a resize mid-zoom.
  function lerpBox(from, to, t) {
    var e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;   // easeInOut
    function mix(a, b) { return a + (b - a) * e; }
    function invMercY(y) {
      return (Math.atan(Math.exp(y * 2 * Math.PI)) * 360) / Math.PI - 90;
    }
    return [
      [mix(from[0][0], to[0][0]), invMercY(mix(mercY(from[0][1]), mercY(to[0][1])))],
      [mix(from[1][0], to[1][0]), invMercY(mix(mercY(from[1][1]), mercY(to[1][1])))],
    ];
  }

  function cameraLerp(spec) {
    if (!spec || !spec.from || !spec.to) return;
    syncSize();
    var t = Math.max(0, Math.min(1, spec.t));
    // An endpoint that IS the chapter's home frame has to resolve to the frame
    // the 'home' camera key actually produces, which is the authored box
    // unioned with everything drawn under it. Lerping to the bare authored box
    // instead makes the frame snap the moment the zoom starts or hands back.
    var box = lerpBox(
      unionBox(spec.from, spec.fromDraws),
      unionBox(spec.to, spec.toDraws),
      t
    );
    // The authored box IS the constraint here: fitBounds never zooms closer
    // than the box needs, so a low cap can only stop the move short of what
    // was asked for. LEG_MAX_ZOOM (12.5) was binding at 1440, holding the
    // frame about 0.13 wider than the Seto-to-Ikeshima box actually allows.
    lastFit = { box: box, maxZoom: ZOOM_MAX };
    map.fitBounds(box, {
      padding: cameraPadding(box),
      maxZoom: ZOOM_MAX,
      duration: 0,
      essential: true,
    });
  }

  function drawPhase(d) {
    // Diagnostic for scripts/scrolly-phase-probe.mjs. Off unless the probe (or
    // ?lcdebug) sets the flag, so production readers pay nothing.
    if (window.LC_SCROLLY_DEBUG) {
      window.__lcPhase = {
        anchor: d.anchor, t: d.t, grow: d.grow || null,
        hold: (d.hold || []).join(','), dash: (d.dash || []).join(','),
        dashGrow: (d.dashGrow || []).join(','), dashT: d.dashT,
        lineAlpha: d.lineAlpha || null,
        pins: (d.pins || []).join(','), camera: d.camera || null,
        cameraDraws: (d.cameraDraws || []).join(',')
      };
      window.__lcPhaseCalls = (window.__lcPhaseCalls || 0) + 1;
    }
    // The wall clock and the scroll-linked path must never both be writing to
    // the same source.
    cancelAnim();
    currentAnchor = d.anchor;
    drawn = {};
    (d.hold || []).forEach(function (id) {
      if (legById(id)) drawn[id] = true;
    });
    (d.dash || []).forEach(function (id) {
      if (legById(id) && (d.dashGrow || []).indexOf(id) < 0) drawn[id] = true;
    });
    var partials = [];
    var growLeg = d.grow ? legById(d.grow) : null;
    if (growLeg) {
      var t = d.t;
      t = typeof t === 'number' ? Math.max(0, Math.min(1, t)) : 0;
      if (t >= 1) drawn[growLeg.id] = true;
      else if (t > 0) partials.push({ leg: growLeg, coords: partialLine(growLeg.coords, growLeg.cum, t) });
    }
    // Dashed legs that draw themselves in. partialLine walks a FRACTION of each
    // leg's own length, so two routes of different lengths leave their ports
    // together and land on the island together.
    var dt = typeof d.dashT === 'number' ? Math.max(0, Math.min(1, d.dashT)) : 1;
    (d.dashGrow || []).forEach(function (id) {
      var leg = legById(id);
      if (!leg) return;
      if (dt >= 1) drawn[leg.id] = true;
      else if (dt > 0) partials.push({ leg: leg, coords: partialLine(leg.coords, leg.cum, dt) });
    });
    setLines(partials, d.lineAlpha);
    setPhasePins(d.pins, d.pinAlpha, d.lineAlpha);
    // Camera keys fire once each. The chapter's own rule is that the map does
    // not move while a line is growing: 'home' fires on entry before any line
    // exists, 'end' only from behind the cover.
    // A scroll-linked zoom owns the camera outright while it runs: the keyed
    // moves would fight it, each one snapping the frame back to its own box.
    if (d.cameraLerp) {
      lastCameraKey = d.cameraLerp.key || 'lerp';
      cameraLerp(d.cameraLerp);
    } else if (d.camera && d.camera !== lastCameraKey) {
      lastCameraKey = d.camera;
      // Both phase camera moves are unseen: 'home' fires on chapter entry
      // before any line exists, 'end' only from behind the cover. Animating
      // either buys nothing and risks the cover lifting mid-flight.
      cameraTo(d.cameraBox, false, d.cameraDraws);
    } else if (!d.camera) {
      lastCameraKey = null;
    }
  }

  function applyPhase(detail) {
    if (!mapEl || !detail || !detail.anchor) return;
    phasedAnchors[detail.anchor] = true;
    if (detail.visible === false) {
      mapEl.classList.remove('is-active');
      return;
    }
    ensurePlaced();
    mapEl.classList.add('is-active');
    if (!isReady) {
      pendingPhase = detail;
      initMap();
      return;
    }
    drawPhase(detail);
  }

  function applyStatic() {
    legs.forEach(function (leg) { drawn[leg.id] = true; });
    setLines(null);
    setPins(Infinity);
    var pts = [];
    legs.forEach(function (leg) { pts = pts.concat(leg.coords); });
    waypoints.forEach(function (wp) { pts.push(wp.coords); });
    framePinFeatures().forEach(function (f) { pts.push(f.geometry.coordinates); });
    if (!pts.length) return;
    if (pts.length === 1) { map.jumpTo({ center: pts[0], zoom: STAY_MAX_ZOOM }); return; }
    map.fitBounds(bboxOf(pts), { padding: 36, maxZoom: LEG_MAX_ZOOM, duration: 0 });
  }

  // ----- Placement ---------------------------------------------------------
  function ensurePlaced() {
    if (placed || !mapEl) return;
    placed = true;
    if (resolveMode() === 'static') {
      mapEl.classList.add('lc-journey-map--static');
      var root = document.getElementById('articleBody');
      var first = null;
      var firstIdx = Infinity;
      legs.forEach(function (leg) {
        if (leg.beatIdx >= 0 && leg.beatIdx < firstIdx) { firstIdx = leg.beatIdx; first = leg; }
      });
      var targetH2 = null;
      if (root && first) {
        Array.prototype.forEach.call(root.querySelectorAll('h2.h2-mark'), function (h2) {
          var ttl = h2.querySelector('.ttl');
          if (slugOf(ttl ? ttl.textContent : h2.textContent) === first.anchor) targetH2 = h2;
        });
      }
      if (root && targetH2) root.insertBefore(mapEl, targetH2);
      else if (root) root.appendChild(mapEl);
      return;
    }
    mapEl.classList.add('lc-journey-map--sticky');
    var imgLayer = document.getElementById('lc-scrolly-img');
    if (imgLayer && imgLayer.parentNode) imgLayer.parentNode.insertBefore(mapEl, imgLayer.nextSibling);
    else document.body.appendChild(mapEl);
  }

  // ----- Init --------------------------------------------------------------
  function initMap() {
    if (initStarted) return;
    initStarted = true;
    ensurePlaced();
    var token = mapEl.getAttribute('data-token') || '';
    if (!token) return;
    loadLibs();
    var tries = 0;
    (function poll() {
      if (typeof window.mapboxgl === 'undefined' || !window.lcMap) {
        if (++tries > 300) return; // ~30s, then give up quietly
        setTimeout(poll, 100);
        return;
      }
      window.mapboxgl.accessToken = token;
      var center = waypoints.length ? waypoints[0].coords : (legs.length ? legs[0].coords[0] : [0, 0]);
      map = window.lcMap.createMap({
        container: canvasEl,
        style: STYLE_URL,
        center: center,
        zoom: 9,
        interactive: false,
        attributionControl: false,
        // Mapbox crossfades a symbol in and out over this many ms as the zoom
        // takes it past a layer's minzoom, past a zoom-stepped filter, or into
        // a collision. At 0 every one of those is a hard cut, which is what
        // makes a scroll-linked zoom read as a set of breakpoints that labels
        // snap across rather than a continuous move. 300 is mapbox's own
        // default. Nothing the probe records is time-based, so its runs stay
        // deterministic.
        fadeDuration: 300,
      }, [mapEl]);
      if (!map) return;
      map.on('load', function () {
        window.lcMap.applyLCMapStyle(map);
        hideBasemapClutter();
        addLayers();
        isReady = true;
        watchViewport();
        if (resolveMode() === 'static') {
          applyStatic();
        } else if (pendingPhase) {
          var d = pendingPhase;
          pendingPhase = null;
          drawPhase(d);
        } else if (pendingAnchor) {
          var a = pendingAnchor;
          pendingAnchor = null;
          applyBeat(a, false);
        }
        mapEl.classList.add('is-revealed');
      });
    })();
  }

  // The light-v11 neighbourhood, minor-settlement and POI labels come through
  // as run-together romaji ("OSETOCHOYUKINOURAKOBUTSUGO") and compete with the
  // waypoints the story names (LOS7-3032). Hidden on this map only; water
  // labels, settlement-major-label and every journey-* layer stay.
  var HIDDEN_BASEMAP_LABELS = /^(settlement-subdivision-label|settlement-minor-label|poi-label)/;
  function hideBasemapClutter() {
    var style = map.getStyle();
    var layers = (style && style.layers) || [];
    for (var i = 0; i < layers.length; i++) {
      if (HIDDEN_BASEMAP_LABELS.test(layers[i].id)) {
        map.setLayoutProperty(layers[i].id, 'visibility', 'none');
      }
    }
  }

  // Which anchors are scroll-phased, read from the MARKUP rather than learned
  // from the first applyPhase call. Learning it late leaves a window on first
  // entry where show() has been called but applyPhase has not: applyBeat's
  // guard is still false, it fits the whole journey, and because that fit
  // ANIMATES over CAMERA_MS it lands after the phase's instant fit and wins.
  // A reader sitting still at the top of the chapter then keeps the wrong
  // frame until they scroll again. It only became visible when the chapter
  // started opening on a close-up: while it opened on the whole-journey frame
  // the stray fit was indistinguishable from the right one.
  function markPhasedAnchors() {
    var blocks = document.querySelectorAll('.lc-scrolly[data-phases][data-anchor]');
    for (var i = 0; i < blocks.length; i++) {
      var a = blocks[i].getAttribute('data-anchor');
      if (a) phasedAnchors[a] = true;
    }
  }

  function show(anchor) {
    if (!mapEl || resolveMode() === 'static') return;
    markPhasedAnchors();
    ensurePlaced();
    mapEl.classList.add('is-active');
    if (!isReady) {
      pendingAnchor = anchor;
      initMap();
      return;
    }
    if (anchor !== currentAnchor) applyBeat(anchor, true);
  }

  function hide() {
    if (!mapEl) return;
    mapEl.classList.remove('is-active');
  }

  // mapbox-gl.js and lc-map.v2.js are injected on demand (LOS7-3032 perf
  // audit) by window.lcLoadMapLibs, defined in article-journey-map.liquid.
  function loadLibs() {
    if (typeof window.lcLoadMapLibs === 'function') window.lcLoadMapLibs();
  }

  // Start the download well before initMap needs it, without initialising.
  function loadLibsWhenNear(target, margin) {
    if (!target || !('IntersectionObserver' in window)) { loadLibs(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      io.disconnect();
      loadLibs();
    }, { rootMargin: margin, threshold: 0 });
    io.observe(target);
  }

  function prefetchWhenNear(target, margin) {
    if (!target) return;
    if (!('IntersectionObserver' in window)) { initMap(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.disconnect();
        // Map init is the page's heaviest main-thread task (WebGL context,
        // style, shaders). Run it when the reader pauses rather than mid-
        // gesture; show() still forces it at once if the map is needed first.
        if ('requestIdleCallback' in window) window.requestIdleCallback(initMap, { timeout: 2000 });
        else initMap();
      });
    }, { rootMargin: margin, threshold: 0 });
    io.observe(target);
  }

  function init() {
    mapEl = document.getElementById('lc-journey-map');
    canvasEl = document.getElementById('lc-journey-map-el');
    if (!mapEl || !canvasEl) return;
    payload = parsePayload();
    if (!payload) { mapEl.style.display = 'none'; return; }
    buildModel();
    if (!legs.length && !waypoints.length) { mapEl.style.display = 'none'; return; }
    ensurePlaced();
    if (resolveMode() === 'static') {
      loadLibsWhenNear(mapEl, '300% 0px 300% 0px');
      prefetchWhenNear(mapEl, '100% 0px 100% 0px');
    } else {
      // Load Mapbox before the reader reaches the first map chapter; show()
      // (from the frame resolver) also kicks init if it arrives first.
      var firstMapBlock = document.querySelector('.lc-scrolly[data-map-chapter]');
      loadLibsWhenNear(firstMapBlock || mapEl, '400% 0px 400% 0px');
      prefetchWhenNear(firstMapBlock || mapEl, '150% 0px 150% 0px');
    }
  }

  ready(function () {
    if (window.LCArticle && window.LCArticle.onReady) window.LCArticle.onReady(init);
    else init();
  });
})();
